// Full month-wise/year-wise Term Loan Repayment Schedule — the detailed
// table a bank actually wants (calendar month labels off a real
// disbursement date, lump-sum or installment-wise drawdown, and a back-ended
// subsidy's prepayment hit), separate from the always-visible year-wise
// "Live Repayment Preview" in the wizard.
import React from 'react';
import { Modal, ModalSection, Field, NextButton, PrimaryButton, Badge, fmt } from './dprFields';
import { repaymentMonths, monthLabel } from './dprLoanCalc';

function yearRows(months) {
  const byYear = {};
  months.forEach(m => {
    const y = Math.ceil(m.month / 12);
    if (!byYear[y]) byYear[y] = [];
    byYear[y].push(m);
  });
  return Object.keys(byYear).map(y => {
    const ms = byYear[y];
    return {
      label: `Year ${y}`,
      openingBalance: ms[0].openingBalance,
      disbursement: ms.reduce((s, m) => s + m.disbursement, 0),
      interest: ms.reduce((s, m) => s + m.interest, 0),
      installment: ms.reduce((s, m) => s + m.installment, 0),
      subsidyRepay: ms.reduce((s, m) => s + m.subsidyRepay, 0),
      closingBalance: ms[ms.length - 1].closingBalance,
    };
  });
}

const COLS = [
  { key: 'label', label: 'Period', align: 'left' },
  { key: 'openingBalance', label: 'Opening Balance' },
  { key: 'disbursement', label: 'Disbursement' },
  { key: 'interest', label: 'Interest' },
  { key: 'installment', label: 'Amount of Installment' },
  { key: 'subsidyRepay', label: 'Repayment by Subsidy' },
  { key: 'closingBalance', label: 'Closing Balance' },
];

async function downloadExcel(rows, filenameBase) {
  const XLSX = await import('xlsx');
  const data = rows.map((r, i) => {
    const row = { 'S.No': i + 1 };
    COLS.forEach(c => { row[c.label] = c.key === 'label' ? r[c.key] : Math.round(r[c.key] || 0); });
    return row;
  });
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Repayment Schedule');
  XLSX.writeFile(wb, `${filenameBase}_Repayment_Schedule.xlsx`);
}

async function downloadPdf(rows, title, filenameBase) {
  const pdfMakeModule = await import('pdfmake/build/pdfmake');
  const pdfFontsModule = await import('pdfmake/build/vfs_fonts');
  const pdfMake = pdfMakeModule.default || pdfMakeModule;
  const pdfFonts = pdfFontsModule.default || pdfFontsModule;
  pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts;

  const inr = n => '₹' + Math.round(n || 0).toLocaleString('en-IN');
  const body = [[
    { text: 'S.No', bold: true, fontSize: 7.5 },
    ...COLS.map(c => ({ text: c.label, bold: true, fontSize: 7.5, alignment: c.align || 'right' })),
  ]];
  rows.forEach((r, i) => body.push([
    { text: String(i + 1), fontSize: 7.5 },
    ...COLS.map(c => ({ text: c.key === 'label' ? r[c.key] : inr(r[c.key]), fontSize: 7.5, alignment: c.align || 'right' })),
  ]));

  const doc = {
    pageSize: 'A4', pageOrientation: 'landscape', pageMargins: [24, 24, 24, 24], defaultStyle: { fontSize: 7.5 },
    content: [
      { text: 'Term Loan Repayment Schedule', bold: true, fontSize: 14, margin: [0, 0, 0, 4] },
      { text: title, fontSize: 9, color: '#666666', margin: [0, 0, 0, 12] },
      { table: { headerRows: 1, widths: ['auto', 'auto', ...COLS.slice(1).map(() => '*')], body }, layout: 'lightHorizontalLines' },
    ],
  };
  await pdfMake.createPdf(doc).download(`${filenameBase}_Repayment_Schedule.pdf`);
}

export function RepaymentScheduleTool({ tl, projectionYears, applicantName, subsidyPrepayment, onSetDisbursementDate }) {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [dateDraft, setDateDraft] = React.useState(tl.disbursementDate || '');
  const [view, setView] = React.useState('month'); // month | year

  const hasLoan = tl.amount > 0 && tl.tenorMonths > 0;
  const needsDate = !tl.disbursementDate;

  const open = () => { setDateDraft(tl.disbursementDate || ''); setModalOpen(true); };
  const confirmDate = () => { onSetDisbursementDate(dateDraft); };

  const months = hasLoan ? repaymentMonths(tl, subsidyPrepayment) : [];
  const monthRows = months.map(m => ({ ...m, label: monthLabel(tl.disbursementDate, m.month) }));
  const rows = view === 'month' ? monthRows : yearRows(months);
  const filenameBase = (applicantName || 'Project').replace(/[^a-z0-9]+/gi, '_');

  if (!hasLoan) return null;

  return (
    <>
      <button type="button" onClick={open}
        style={{ background: 'linear-gradient(135deg, #ff5200, #e04800)', boxShadow: '0 2px 8px rgba(255,82,0,0.25)' }}
        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-white text-xs font-bold hover:shadow-md hover:-translate-y-0.5 transition-all">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>
        Repayment Schedule
      </button>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)}
        title="Term Loan Repayment Schedule" subtitle={tl.disbursementDate ? undefined : 'Enter the initial disbursement date to generate the month-wise schedule'}
        maxWidth={tl.disbursementDate ? '1100px' : '480px'}>
        {needsDate && !tl.disbursementDate ? (
          <ModalSection title="Initial Disbursement Date">
            <Field label="Disbursement Date" hint="The month-wise schedule is built off this date, considering the moratorium period.">
              <input type="date" value={dateDraft} onChange={e => setDateDraft(e.target.value)}
                className="cx-input w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-100 focus:outline-none focus:border-[#ff5200]" />
            </Field>
            <div className="flex justify-end mt-2">
              <NextButton onClick={confirmDate} disabled={!dateDraft}>Generate Schedule</NextButton>
            </div>
          </ModalSection>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setView('month')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${view === 'month' ? 'bg-[#ff5200] text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'}`}>Month-wise</button>
                <button type="button" onClick={() => setView('year')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${view === 'year' ? 'bg-[#ff5200] text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'}`}>Year-wise</button>
                <Badge tone="slate">Disbursed {tl.disbursementDate} · <button type="button" className="underline ml-1" onClick={() => onSetDisbursementDate(null)}>change</button></Badge>
              </div>
              <div className="flex items-center gap-2">
                <PrimaryButton onClick={() => downloadExcel(rows, filenameBase)} variant="orange">⬇ Excel</PrimaryButton>
                <PrimaryButton onClick={() => downloadPdf(rows, applicantName, filenameBase)}>⬇ PDF</PrimaryButton>
              </div>
            </div>
            <div className="overflow-auto max-h-[55vh] rounded-xl border border-slate-100 dark:border-slate-700">
              <table className="w-full text-xs border-collapse">
                <thead className="sticky top-0">
                  <tr className="bg-slate-50 dark:bg-slate-900">
                    <th className="text-left px-3 py-2 border-b border-slate-200 dark:border-slate-700 font-bold text-slate-500 uppercase tracking-wide text-[10px]">S.No</th>
                    {COLS.map(c => (
                      <th key={c.key} className={`px-3 py-2 border-b border-slate-200 dark:border-slate-700 font-bold text-slate-500 uppercase tracking-wide text-[10px] ${c.align === 'left' ? 'text-left' : 'text-right'}`}>{view === 'month' && c.key === 'label' ? 'Month' : view === 'year' && c.key === 'label' ? 'Year' : c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="hover:bg-slate-50/60 dark:hover:bg-slate-900/30">
                      <td className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800">{i + 1}</td>
                      {COLS.map(c => (
                        <td key={c.key} className={`px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 ${c.align === 'left' ? 'text-left' : `text-right ${c.key === 'closingBalance' ? 'font-semibold' : ''}`}`}>
                          {c.key === 'label' ? r[c.key] : `₹${fmt(r[c.key])}`}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
